import { AfterViewChecked, Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgSelectComponent } from '@ng-select/ng-select';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap} from 'rxjs/operators';
import moment from 'moment';
import { LoadingController, ModalController } from '@ionic/angular';
import { BackendCallService } from 'src/app/services/backend-call/backend-call.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { ShareValuesService } from 'src/app/services/sharedValues/share-values.service';
import { AppConstants } from 'src/app/utilities/constant';
import { AlertModalPage } from 'src/app/components/alert-modal/alert-modal.page';
import { MachineAttribute } from 'src/app/pages/project-management/machine-details/machine-details-interface';

const NO_MACHINE_STATUS_ID = '1';
const NO_MACHINE_SECTOR_ID = 'FDEB094A-D3FB-4B1D-81AB-5626340CE390';

@Component({
  selector: 'app-machine-details',
  templateUrl: './machine-details.component.html',
  styleUrls: ['./machine-details.component.scss'],
})
export class MachineDetailsComponent implements OnInit, OnChanges, AfterViewChecked {
  @ViewChild('machineForm', { static: false }) machineForm: any;
  @ViewChild('machineMode') machineMode: NgSelectComponent;
  @Input() global_Id: string;
  @Input() machineId: string; 
  @Input() isEditMachine: boolean;
  @Input() isCloneMode = false;
  @Input() cloneSourceMachineId = '';
  @Input() cloneCustomerId = '';
  @Input() cloneCustomerName = '';
  @Input() cloneMachineName = '';
  @Input() isFromProject = false;
  @Input() projectId = '';
  @Input() serviceId = '';
  public minDate: string = new Date().toISOString();
  public attributesData: any
  public formData: any;
  public isSaveDisabled = true;
  private originalFormData: any;
  public contactList: Subject<string[]> = of([]) as Subject<string[]>;
  public contactInput$ = new Subject<string>();
  public startMinDate = moment().format('YYYY-MM-DD');
  public endMinDate = moment().format('YYYY-MM-DD');
  public placingDateNotAvailable: boolean = false;
  public inspectionDateNotAvailable: boolean = false;
  public descMaxLength = 2000;

  constructor(
    public backendService: BackendCallService,
    public sharedValue: ShareValuesService,
    public loading: LoadingController,
    private modalController: ModalController,
    private toastService: ToastService,
    public translate: TranslateService,
    private router: Router,
    private elementRef: ElementRef<HTMLElement>,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.isCloneMode && !changes.isCloneMode.firstChange && !this.isCloneMode)
      || (changes.machineId && !changes.machineId.firstChange && changes.machineId.previousValue !== this.machineId)) {
      this.formData = null;
      this.getMachineAttributes();
      return;
    }
    if (this.isCloneMode && this.formData && (changes.cloneMachineName || changes.cloneCustomerId || changes.cloneCustomerName)) {
      this.applyCloneFormValues();
    }
  }

  ngOnInit() {
    this.getMachineAttributes();
    this.contactList = this.contactInput$.pipe(
      debounceTime(600),
      distinctUntilChanged(),
      switchMap((inp: string) => {
        this.formData.customerLoading = true;
        let obs: Observable<Object>;
        if (inp) {
          return this.backendService.searchServiceCustomerName(inp).pipe(
            map((res: any) => res.value),
            catchError((err) => []),
            finalize(() => {
              this.formData.customerLoading = false;
            })
          );
        } else {
          return of([]).pipe(
            finalize(() => {
              this.formData.customerLoading = false;
            })
          );
        }
      })
    ) as Subject<string[]>;
  }

  ngAfterViewChecked(): void {
    if (this.isEditMachine) {
      this.ensureNgSelectClearButtonsAccessible();
    }
  }

  private ensureNgSelectClearButtonsAccessible(): void {
    const host = this.elementRef?.nativeElement;
    if (!host) {
      return;
    }

    const clearButtons = host.querySelectorAll<HTMLElement>('ng-select .ng-clear-wrapper');
    if (!clearButtons.length) {
      return;
    }

    clearButtons.forEach(button => {
      button.tabIndex = 0;
      button.setAttribute('role', 'button');
      button.setAttribute('aria-label', this.translate.instant('Clear selection'));

      const clearIcon = button.querySelector<HTMLElement>('.ng-clear');
      if (clearIcon) {
        clearIcon.tabIndex = -1;
        clearIcon.removeAttribute('role');
        clearIcon.removeAttribute('aria-label');
      }

      if (button.getAttribute('data-keyboard-wired') === 'true') {
        return;
      }

      const clearHandler = (event: KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const select = button.closest('ng-select') as HTMLElement | null;
        const field = select?.getAttribute('data-field');
        if (field && this.formData) {
          (this.formData as any)[field] = '';
          if (field === 'customerName') {
            this.formData.customerId = '';
          }
          this.onFormChange();
        }

        const container = button.closest('.ng-select-container') as HTMLElement;
        container?.focus();
      };

      button.addEventListener('keydown', clearHandler, true);

      button.setAttribute('data-keyboard-wired', 'true');
    });

  }

  /**
   * This method is called when the user clicks on the edit button.
   */
  onEdit() {
    this.isEditMachine = true;
  }

  /**
   * This method initializes the form data with the machine details.
   */
  initializeForm() {
    const noMachineModes = !this.attributesData.machineModes || this.attributesData.machineModes.length === 0;
    const noMachineModeId = '0'; // must match the ID in api response for Not Available
    const noMachineStatus = !this.attributesData?.machine_Status;
    const noMachineStatusId = NO_MACHINE_STATUS_ID; // must match the ID in api response for Not Available
    const noMachineSector = !this.attributesData?.industry_type;
    const noMachineSectorId = NO_MACHINE_SECTOR_ID; // must match the ID in api response for Not Available
    this.formData = {
      machineName: this.attributesData?.machine_name,
      customerName: this.attributesData?.customer_Name || '',
      customerId: this.attributesData?.customer_Id || '',
      placingDate: this.formatDate(this.attributesData?.date_of_placing_of_market) || '',
      sectorIndustry: noMachineSector ? noMachineSectorId : this.attributesData?.industry_type,
      location: this.normalizeOptionalTextField(this.attributesData?.machine_location),
      inspectionDate: this.formatDate(this.attributesData?.date_of_inspection) || '',
      certification: this.normalizeOptionalTextField(this.attributesData?.certificate),
      machineStatus: noMachineStatus ? noMachineStatusId : this.attributesData.machine_Status,
      machineModes: noMachineModes ? [noMachineModeId] : this.attributesData.machineModes,
      description: this.normalizeOptionalTextField(this.attributesData?.machine_Description ?? this.attributesData?.description)
    };
    this.applyCloneFormValues();
    this.originalFormData = JSON.parse(JSON.stringify({
      attributesData: this.attributesData,
      formData: this.formData
    }));
    this.isSaveDisabled = true;
    if (!this.formData.inspectionDate || this.formData.inspectionDate.trim() === '') {
      this.inspectionDateNotAvailable = true;
      this.onFormChange();
    }
    if (!this.formData.placingDate || this.formData.placingDate.trim() === '') {
      this.placingDateNotAvailable = true;
    }
  }

  private applyCloneFormValues(): void {
    if (!this.isCloneMode || !this.formData) {
      return;
    }

    this.formData.machineName = this.cloneMachineName || this.formData.machineName;
    this.formData.customerId = this.cloneCustomerId || this.formData.customerId;
    this.formData.customerName = this.cloneCustomerName || this.formData.customerName;
  }

  /**
   * This method returns the selected machine status as a string to display on view screen.
   */
  get selectedMachineStatus(): string {
    const statusId = this.formData?.machineStatus;
    const allStatuses = this.sharedValue?.machineStatusList || [];
    const match = allStatuses.find(status => status.id === statusId);
    return match?.name || this.translate.instant('not_available');
  }

  /**
   * This method returns the selected sector industry as a string to display on view screen.
   */
  get selectedSectorIndustry(): string | null {
    const sectorId = this.formData?.sectorIndustry;
    const allSectors = this.sharedValue?.sectorIndustryList || [];
    const match = allSectors.find(sector => sector.id === sectorId);
    return match?.name || this.translate.instant('not_available');
  }

  private isNotAvailableText(value?: string | null): boolean {
    if (typeof value !== 'string') {
      return false;
    }

    const normalizedValue = value.trim().toLowerCase();
    const translatedNotAvailable = (this.translate.instant('not_available') || '').trim().toLowerCase();

    return normalizedValue === 'not available'
      || normalizedValue === 'not_available'
      || (!!translatedNotAvailable && normalizedValue === translatedNotAvailable);
  }

  private normalizeOptionalTextField(value?: string | null): string {
    if (!value || this.isNotAvailableText(value)) {
      return this.translate.instant('not_available');
    }

    return value;
  }

  private serializeOptionalTextField(value?: string | null): string {
    if (!value || !value.trim() || this.isNotAvailableText(value)) {
      return '';
    }

    return value;
  }

  /**
   * This method is called when the form data changes.
   */
  onFormChange(): void {
    if (!this.formData.inspectionDate || this.formData.inspectionDate.trim() === '') {
      this.formData.inspectionDate = null;
    }
    const current = JSON.stringify({
      attributesData: this.attributesData,
      formData: this.formData
    });
    const original = JSON.stringify(this.originalFormData);
    const isFormUnchanged = current === original;
    let isFormInvalid = this.machineForm?.invalid;
    if (this.inspectionDateNotAvailable) {
      isFormInvalid = false;
    } else {
      isFormInvalid = !this.machineForm?.controls?.inspectionDate?.value;
    }
    const noMachineModesSelected = !this.formData.machineModes || this.formData.machineModes.length === 0;
    const customerName = (this.formData.customerName || '').toString().trim();
    const noCustomerName = customerName === '';
    const noMachineName = !(this.formData.machineName || '').toString().trim();
    this.isSaveDisabled = isFormUnchanged || isFormInvalid || noMachineModesSelected || noCustomerName || noMachineName;
  }

  /**
   * This method is called when the user toggles the date checkbox.
   * @param event 
   * @param field 
   */
  onDateCheckboxToggle(event: Event, field: 'placingDate' | 'inspectionDate') {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (field === 'placingDate') {
      this.placingDateNotAvailable = isChecked;
      if (isChecked) {
        this.formData.placingDate = null;
        if (this.attributesData?.date_of_inspection) {
          this.formData.inspectionDate = this.formatDate(this.formData.inspectionDate || this.attributesData?.date_of_inspection || '');
        }
      } else {
        this.formData.placingDate = this.formatDate(this.attributesData?.date_of_placing_of_market || '');
      }
    }
    if (field === 'inspectionDate') {
      this.inspectionDateNotAvailable = isChecked;
      if (isChecked) {
        this.formData.inspectionDate = null;
      } else {
        this.formData.inspectionDate = this.formatDate(this.attributesData?.date_of_inspection || '');
      }
    }
    this.onFormChange();
  }

  onDateCheckboxToggleByEnter(event: KeyboardEvent, field: 'placingDate' | 'inspectionDate'): void {
    event.preventDefault();
    const isChecked = field === 'placingDate' ? !this.placingDateNotAvailable : !this.inspectionDateNotAvailable;
    this.onDateCheckboxToggle({ target: { checked: isChecked } } as unknown as Event, field);
  }

  onKeydown(event: KeyboardEvent): void {
    // Keep native Tab behavior for date inputs; this prevents template runtime errors.
    if (event.key === 'Tab') {
      return;
    }
  }

  /**
   * This method is called when the user clicks on the cancel button.
   */
  async onCancel() {
    let msg: string;
    this.translate.get(AppConstants.ARE_YOU_SURE_TO_CANCEL).subscribe(res => {
      msg = res;
    });
    const props = { alertContent: msg , showCancelButton: true};
    const modal = await this.modalController.create({
      component: AlertModalPage,
      cssClass: 'small-alert-modal',
      backdropDismiss: false,
      showBackdrop: false,
      componentProps: props,
    });
    await modal.present();
    const { data: { isConfirmed } } = await modal.onWillDismiss();
    if (isConfirmed) {
      this.isEditMachine = false;
      this.getMachineAttributes();
    }
  }

  /**
   * This method returns a warning message if the machine description exceeds the maximum length.
   * @returns 
   */
  getMachineDescriptionCharacterWarning(): string | null {
    if (this.formData?.description && this.formData.description.length > this.descMaxLength) {
      const LimitText = this.translate.instant('character_limit_exceeded');
      return `${LimitText} (${this.formData.description.length}/${this.descMaxLength})`;
    }
    return null;
  }

  /**
   * This method fetches the machine attributes data from the backend service.
   */
  getMachineAttributes() {
    this.backendService.getMachinesAttributesData(this.machineId).subscribe((attributeDataResult: MachineAttribute) => {
      this.attributesData = attributeDataResult.value;
      this.attributesData.machineMetaData=attributeDataResult.value.machineMetaData;
      this.initializeForm();
    });
  }

  /**
   * This method is called when the user clicks on the save button.
   */
  onSave() {
    if (this.isCloneMode) {
      return;
    }
    let updatedAttributes: any;
    // Refetch so Attribute Details fields saved from the other tab aren't overwritten with stale values.
    this.backendService.getMachinesAttributesData(this.machineId).pipe(
      switchMap((latestResult: MachineAttribute) => {
        const latest = latestResult?.value || this.attributesData || {};
        const latestMetaData = latest.machineMetaData || this.attributesData?.machineMetaData;
        const payload = {
          id: this.machineId,
          machine_Description: this.serializeOptionalTextField(this.formData?.description),
          machineName: this.formData.machineName,
          customerId: this.formData?.customerId || '',
          customer_Name: this.formData.customerName,
          date_of_placing_of_market: this.isPlacingDateUnchanged()
            ? latestResult?.value?.date_of_placing_of_market || null
            : this.formData?.placingDate || null,
          industry_type: this.formData?.sectorIndustry || NO_MACHINE_SECTOR_ID,
          machine_location: this.serializeOptionalTextField(this.formData?.location),
          date_of_inspection: this.isInspectionDateUnchanged()
            ? latestResult?.value?.date_of_inspection || null
            : this.formData?.inspectionDate || null,
          certificate: this.serializeOptionalTextField(this.formData?.certification),
          machine_Status: this.formData?.machineStatus || NO_MACHINE_STATUS_ID,
          machineModes: this.formData?.machineModes || '',
          description: this.serializeOptionalTextField(this.formData?.description),
          application: latestMetaData?.application?.id || '',
          machineType: latestMetaData?.machineType?.id || '',
          specificFeature: (latestMetaData?.specificFeature || []).map((feature => feature.id)),
          controlSystem: (latestMetaData?.controlSystem || []).map((controlSys => controlSys.id)),
        };
        updatedAttributes = {
          ...latest,
          machine_Description: payload.machine_Description,
          description: payload.description,
          machine_location: payload.machine_location,
          certificate: payload.certificate
        };
        return this.backendService.editMachineLevelAttributes(payload);
      })
    ).subscribe((data: any) => {
      if (data.statusCode === 200) {
        this.toastService.presentToast(
          this.translate.instant('customer_management_updated_successfully', { entity: this.translate.instant('Machine Details') }),
          4000,
          AppConstants.SUCCESS_SNACKBAR,
          'top'
        );
        this.attributesData = updatedAttributes;
        this.formData.location = this.normalizeOptionalTextField(updatedAttributes.machine_location);
        this.formData.certification = this.normalizeOptionalTextField(updatedAttributes.certificate);
        this.formData.description = this.normalizeOptionalTextField(
          updatedAttributes.machine_Description ?? updatedAttributes.description
        );
      } else {
        this.toastService.presentToast(data.message, 4000, AppConstants.ERROR_SNACKBAR, 'top');
      }
      this.isEditMachine = false;
    }, err => {
      this.getMachineAttributes();
      this.isEditMachine = false;
    });
  }

  private isInspectionDateUnchanged(): boolean {
    return (this.formData?.inspectionDate || null) === (this.originalFormData?.formData?.inspectionDate || null);
  }

  private isPlacingDateUnchanged(): boolean {
    return (this.formData?.placingDate || null) === (this.originalFormData?.formData?.placingDate || null);
  }

  onClone(machineImages: any[] = [], cloneAttributeDetails?: any): void {
    if (!this.formData || !this.attributesData) {
      this.toastService.presentToast(this.translate.instant('Required Fields Not Selected'), 4000, AppConstants.WARNING_SNACKBAR, 'top');
      return;
    }

    const machineDetails = this.buildCloneMachineDetailsPayload(cloneAttributeDetails);
    const validationMessage = this.getCloneValidationMessage(machineDetails);
    if (validationMessage) {
      this.toastService.presentToast(validationMessage, 4000, AppConstants.WARNING_SNACKBAR, 'top');
      return;
    }

    const payload = {
      copy_From_Machine_Id: this.cloneSourceMachineId || this.machineId,
      service_Id: this.isFromProject ? this.serviceId || '' : '',
      is_From_Project: this.isFromProject,
      project_Id: this.isFromProject ? this.projectId || '' : '',
      machineDetails,
      machineLimits: this.buildEmptyCloneMachineLimitsPayload(),
      machineImages
    };

    this.backendService.cloneMachine(payload).subscribe({
      next: (response: any) => {
        if (response?.statusCode === 200 || response?.flag === true || response?.success === true) {
          this.toastService.presentToast(this.translate.instant('Machines Added Successfully'), 4000, AppConstants.SUCCESS_SNACKBAR, 'top');
          this.isCloneMode = false;
          this.isEditMachine = false;
          if (this.isFromProject && this.projectId) {
            this.router.navigate(['dashboard/project-detail', this.projectId], { queryParamsHandling: 'preserve' });
          } else {
            this.router.navigate(['/machine-management']);
          }
        } else {
          this.toastService.presentToast(response?.message || this.translate.instant('something_went_wrong'), 4000, AppConstants.ERROR_SNACKBAR, 'top');
        }
      },
      error: (error: any) => {
        if (error?.status === 400) {
          return;
        }
        this.toastService.presentToast(error?.error?.message || this.translate.instant('something_went_wrong'), 4000, AppConstants.ERROR_SNACKBAR, 'top');
      }
    });
  }

  private buildCloneMachineDetailsPayload(cloneAttributeDetails?: any): any {
    const machineMetaData = this.attributesData?.machineMetaData || {};
    const hasCloneAttributeDetails = !!cloneAttributeDetails;
    const sourceSpecificFeatureIds = Array.isArray(machineMetaData?.specificFeature)
      ? machineMetaData.specificFeature.map((feature: any) => feature?.id || feature).filter(Boolean)
      : [];
    const sourceControlSystemIds = Array.isArray(machineMetaData?.controlSystem)
      ? machineMetaData.controlSystem.map((controlSys: any) => controlSys?.id || controlSys).filter(Boolean)
      : [];

    return {
      id: '',
      machineName: this.formData?.machineName || '',
      customerId: this.formData?.customerId || '',
      machine_Description: this.serializeOptionalTextField(
        this.formData?.description
      ),
      serial_Number: hasCloneAttributeDetails ? cloneAttributeDetails.serial_Number || '' : '',
      asset_Number: hasCloneAttributeDetails ? cloneAttributeDetails.asset_Number || '' : '',
      machine_Status: this.formData?.machineStatus || NO_MACHINE_STATUS_ID,
      manufacturer: hasCloneAttributeDetails ? cloneAttributeDetails.manufacturer || '' : this.attributesData?.manufacturer || '',
      machine_Model: hasCloneAttributeDetails ? cloneAttributeDetails.machine_Model || '' : '',
      application: hasCloneAttributeDetails ? cloneAttributeDetails.application || '' : machineMetaData?.application?.id || this.attributesData?.application || '',
      machineType: hasCloneAttributeDetails ? cloneAttributeDetails.machineType || '' : machineMetaData?.machineType?.id || this.attributesData?.machineType || '',
      specificFeature: hasCloneAttributeDetails ? cloneAttributeDetails.specificFeature || [] : sourceSpecificFeatureIds,
      controlSystem: hasCloneAttributeDetails ? cloneAttributeDetails.controlSystem || [] : sourceControlSystemIds,
      machineModes: Array.isArray(this.formData?.machineModes)
        ? this.formData.machineModes
        : this.formData?.machineModes ? [this.formData.machineModes] : [],
      machine_location: this.serializeOptionalTextField(this.formData?.location),
      industry_type: this.formData?.sectorIndustry || NO_MACHINE_SECTOR_ID,
      certificate: this.serializeOptionalTextField(this.formData?.certification),
      date_of_placing_of_market: this.formData?.placingDate || null,
      date_of_inspection: this.formData?.inspectionDate || null
    };
  }

  private getCloneValidationMessage(machineDetails: any): string {
    const missingFields = [];
    if (!machineDetails?.machineName) {
      missingFields.push(this.translate.instant('machine_name'));
    }
    if (!machineDetails?.customerId) {
      missingFields.push(this.translate.instant('Customer Name'));
    }
    if (!machineDetails?.application) {
      missingFields.push(this.translate.instant('Application'));
    }
    if (!Array.isArray(machineDetails?.specificFeature) || machineDetails.specificFeature.length === 0) {
      missingFields.push(this.translate.instant('Specific Feature'));
    }
    if (!Array.isArray(machineDetails?.controlSystem) || machineDetails.controlSystem.length === 0) {
      missingFields.push(this.translate.instant('Control System'));
    }

    return missingFields.length
      ? `${this.translate.instant('Required Fields Not Selected')} ${missingFields.join(', ')}`
      : '';
  }

  private buildEmptyCloneMachineLimitsPayload(): any {
    return {
      machineId: '',
      is_limit_visible: false,
      userId: '',
      limits: [],
      machineModeList: []
    };
  }

  /**
   * This method is called when the user selects a customer from the dropdown.
   * @param customer 
   */
  customerSearchByErpId(customer) {
    this.getCustomerAndSegment(customer).subscribe((responses: any) => {
      if (responses && responses.length) {
        const customerInfo = responses[0];
        const segmentInfo = responses[1].result;
        customerInfo.contacts.map((i) => { i.fullName = (i.first_Name != null ? i.first_Name : '') + ' ' + (i.last_Name != null ? i.last_Name : ''); return i; });
        this.sharedValue.customersInfo.next(customerInfo);
        this.formData.customerId = customerInfo?.customer_Id;
        this.formData.customerName = customerInfo?.customer_Name;
        this.formData.industrySegment = segmentInfo[0]?.segment;
      }
    }, err => {
      this.toastService.presentToast(this.translate.instant('no_results_found'), 4000, AppConstants.ERROR_SNACKBAR, 'top');   
    });
  }

  /**
   * This method fetches customer and segment information based on the ERP ID.
   * @param cust 
   * @returns 
   */
  getCustomerAndSegment(cust) {
    const getCustomer = this.backendService.offerCustomerInfoSearchByErpId(cust.erp_customer_id);
    const getSegment = this.backendService.getSegments(cust.erp_customer_id);
    return forkJoin([getCustomer, getSegment]);
  }

  /**
   * This method is called when the user selects a date in the date picker.
   */
  onChangeDate() {
    this.endMinDate = moment(this.convertDateFormat(this.formData.startDate)).format('YYYY-MM-DD');
    if (moment(this.startMinDate).isBefore(this.endMinDate)) {
      this.formData.end_Date = '';
    }
  }

  /**
   * This method converts the date format from DD/MM/YYYY to YYYY-MM-DD.
   * @param date 
   * @returns 
   */
  convertDateFormat(date) {
    if (date) {
      const spilitedDate = date.split('/');
      return `${spilitedDate[2]}-${spilitedDate[1]}-${spilitedDate[0]}`;
    } else {return date;}
  }

  /**
   * This method formats a date string to 'YYYY-MM-DD' format.
   * @param dateString 
   * @returns 
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const dateOnlyMatch = /^(\d{4}-\d{2}-\d{2})(?:$|T)/.exec(dateString);
    if (dateOnlyMatch) {
      return dateOnlyMatch[1];
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = (`0${date.getMonth() + 1}`).slice(-2);
    const day = (`0${date.getDate()}`).slice(-2);
    return `${year}-${month}-${day}`;
  }

}